<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Eye, RefreshCw, RotateCcw, Search, Sparkles } from '@lucide/vue'
import {
  Badge, Button, EmptyState, Input, Pagination, Section, Select,
  Spinner, Stat, Table, Toolbar, useToast,
} from '@drama/ui'

import {
  autoGenerateIntentVideoRequest, listIntentVideoQueue, rePromptIntentVideoRequest, retryIntentVideoRequest,
} from '../../api/intent-videos.js'
import { formatDate, pageSummary, queueStatusLabel, queueStatusVariant } from './format.js'

const props = defineProps({ scenarioId: { type: String, required: true } })

const { t } = useI18n()
const toast = useToast()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const items = ref([])
const counts = ref({ pending: 0, prompting: 0, prompt_ready: 0, rendering: 0, done: 0, failed: 0 })
const filters = reactive({ status: '', source: '', q: '' })
const sort = reactive({ key: 'updated_at', order: 'desc' })
const pageSize = ref(50)
/** 游标栈：Pagination 只给方向，页面自己记住怎么回去。 */
const cursors = ref([])
const nextCursor = ref(null)
const retryingId = ref('')
const rePromptingId = ref('')
const autoGeneratingId = ref('')

const statusOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.intentVideo.filters.allStatuses') },
  ...['pending', 'prompting', 'prompt_ready', 'rendering', 'done', 'failed']
    .map(status => ({ value: status, label: queueStatusLabel(t, status) })),
])

const sourceOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.intentVideo.filters.allSources') },
  { value: 'cluster', label: t('visualGameAdmin.intentVideo.source.cluster') },
  { value: 'gameplay', label: t('visualGameAdmin.intentVideo.source.gameplay') },
  { value: 'admin', label: t('visualGameAdmin.intentVideo.source.admin') },
])

const columns = computed(() => [
  { key: 'status', label: t('visualGameAdmin.intentVideo.table.status'), width: '110px', sortable: true },
  { key: 'intent_text', label: t('visualGameAdmin.intentVideo.table.intent'), width: '320px' },
  { key: 'occurrences', label: t('visualGameAdmin.intentVideo.table.occurrences'), width: '90px', align: 'right', mono: true, sortable: true },
  { key: 'source', label: t('visualGameAdmin.intentVideo.table.source'), width: '90px' },
  { key: 'attempt', label: t('visualGameAdmin.intentVideo.table.attempt'), width: '70px', align: 'right', mono: true, sortable: true },
  { key: 'last_error', label: t('visualGameAdmin.intentVideo.table.lastError'), width: '260px' },
  { key: 'updated_at', label: t('visualGameAdmin.intentVideo.table.updatedAt'), width: '170px', sortable: true },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '230px', align: 'right', fixed: 'right' },
])

const summary = computed(() => pageSummary(t, { loaded: items.value.length, hasNext: nextCursor.value !== null }))

onMounted(() => load(null))

async function load(cursor) {
  loading.value = true
  error.value = ''
  try {
    const data = await listIntentVideoQueue({
      scenario_id: props.scenarioId,
      limit: pageSize.value,
      cursor: cursor ?? undefined,
      status: filters.status || undefined,
      source: filters.source || undefined,
      q: filters.q.trim() || undefined,
      sort: sort.key,
      order: sort.order,
    })
    items.value = data.items
    counts.value = data.counts
    nextCursor.value = data.next_cursor
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function applyFilters() {
  cursors.value = []
  load(null)
}

function resetFilters() {
  filters.status = ''
  filters.source = ''
  filters.q = ''
  applyFilters()
}

function nextPage() {
  cursors.value.push(nextCursor.value)
  load(nextCursor.value)
}

function prevPage() {
  cursors.value.pop()
  load(cursors.value[cursors.value.length - 1] ?? null)
}

function changePageSize(size) {
  pageSize.value = size
  applyFilters()
}

function changeSort(key) {
  sort.order = sort.key === key && sort.order === 'desc' ? 'asc' : 'desc'
  sort.key = key
  applyFilters()
}

async function retry(row) {
  retryingId.value = row.id
  try {
    await retryIntentVideoRequest(row.id)
    toast.success(t('visualGameAdmin.intentVideo.queue.retried'))
    await load(cursors.value[cursors.value.length - 1] ?? null)
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    retryingId.value = ''
  }
}

/**
 * 按当前规范重写。和 retry 不是一件事：retry 接着老提示词老参数跑，这条是回队首重写。
 * 旧片子留在召回池顶着，新的出来之前覆盖率不掉。
 */
async function rePrompt(row) {
  rePromptingId.value = row.id
  try {
    await rePromptIntentVideoRequest(row.id)
    toast.success(t('visualGameAdmin.intentVideo.queue.rePrompted'))
    await load(cursors.value[cursors.value.length - 1] ?? null)
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    rePromptingId.value = ''
  }
}

/** pending 一键跑到出片：认领 + 调 lemonboss 写提示词 + 出片全在后台做，接口立刻返回。 */
async function autoGenerate(row) {
  autoGeneratingId.value = row.id
  try {
    await autoGenerateIntentVideoRequest(row.id)
    toast.success(t('visualGameAdmin.intentVideo.queue.autoGenerated'))
    await load(cursors.value[cursors.value.length - 1] ?? null)
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    autoGeneratingId.value = ''
  }
}

function openDetail(row) {
  router.push({ name: 'intent-video-queue-detail', params: { requestId: row.id } })
}
</script>

<template>
  <div class="ivq vga-page" data-insp-path="apps/visual-game-admin/src/pages/intent-video/QueueTab.vue#queue">
    <Section>
      <div class="ivq__stats">
        <Stat v-for="status in ['pending', 'prompting', 'prompt_ready', 'rendering', 'done', 'failed']"
              :key="status"
              :label="queueStatusLabel(t, status)"
              :value="String(counts[status] ?? 0)" />
      </div>
    </Section>

    <Section>
      <Toolbar>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.intentVideo.filters.search') }}</label>
          <Input
            v-model="filters.q"
            type="search"
            size="sm"
            :placeholder="t('visualGameAdmin.intentVideo.filters.queueSearchPlaceholder')"
            @keyup.enter="applyFilters"
          />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.intentVideo.table.status') }}</label>
          <Select v-model="filters.status" :options="statusOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.intentVideo.table.source') }}</label>
          <Select v-model="filters.source" :options="sourceOptions" size="sm" />
        </div>
        <template #actions>
          <Button size="sm" :loading="loading" @click="applyFilters">
            <Search :size="14" /> {{ t('visualGameAdmin.intentVideo.filters.apply') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="loading" @click="resetFilters">
            <RotateCcw :size="14" /> {{ t('visualGameAdmin.common.reset') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="load(null)">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <template v-else>
      <Section :padded="false">
        <EmptyState
          v-if="items.length === 0"
          :title="t('visualGameAdmin.intentVideo.queue.emptyTitle')"
          :description="t('visualGameAdmin.intentVideo.queue.emptyDescription')"
        />
        <Table
          v-else
          :columns="columns"
          :rows="items"
          clickable
          :sort-key="sort.key"
          :sort-order="sort.order"
          @row-click="openDetail"
          @sort="changeSort"
        >
          <template #status="{ row }">
            <Badge :variant="queueStatusVariant(row.status)" size="sm">{{ queueStatusLabel(t, row.status) }}</Badge>
          </template>
          <template #intent_text="{ row }">
            <span class="ivq__clamp" :title="row.intent_text">{{ row.intent_text }}</span>
          </template>
          <template #source="{ row }">{{ t(`visualGameAdmin.intentVideo.source.${row.source}`) }}</template>
          <template #last_error="{ row }">
            <span class="ivq__ellipsis" :title="row.last_error || ''">{{ row.last_error || '-' }}</span>
          </template>
          <template #updated_at="{ row }">{{ formatDate(row.updated_at) }}</template>
          <template #actions="{ row }">
            <div class="ivq__actions">
              <Button variant="secondary" size="sm" @click.stop="openDetail(row)">
                <Eye :size="14" /> {{ t('visualGameAdmin.common.view') }}
              </Button>
              <Button
                v-if="row.status === 'pending'"
                variant="ghost"
                size="sm"
                :loading="autoGeneratingId === row.id"
                @click.stop="autoGenerate(row)"
              >
                <Sparkles :size="14" /> {{ t('visualGameAdmin.intentVideo.queue.autoGenerate') }}
              </Button>
              <Button
                v-if="row.status === 'failed'"
                variant="ghost"
                size="sm"
                :loading="retryingId === row.id"
                @click.stop="retry(row)"
              >
                <RotateCcw :size="14" /> {{ t('visualGameAdmin.common.retry') }}
              </Button>
              <!-- 已经绑了资源的才谈得上「重写」；pending 本来就要重写，不用这个按钮。 -->
              <Button
                v-if="row.resource_id"
                variant="ghost"
                size="sm"
                :loading="rePromptingId === row.id"
                :title="t('visualGameAdmin.intentVideo.queue.rePromptHint')"
                @click.stop="rePrompt(row)"
              >
                <RefreshCw :size="14" /> {{ t('visualGameAdmin.intentVideo.queue.rePrompt') }}
              </Button>
            </div>
          </template>
        </Table>
      </Section>
      <Pagination
        :has-prev="cursors.length > 0"
        :has-next="nextCursor !== null"
        :loading="loading"
        :summary="summary"
        :prev-label="t('visualGameAdmin.intentVideo.pager.prev')"
        :next-label="t('visualGameAdmin.intentVideo.pager.next')"
        :page-size="pageSize"
        :page-size-options="[20, 50, 100]"
        :page-size-label="t('visualGameAdmin.intentVideo.pager.pageSize')"
        @prev="prevPage"
        @next="nextPage"
        @update:page-size="changePageSize"
      />
    </template>
  </div>
</template>

<style scoped>
.ivq__actions { display: flex; gap: var(--drama-space-xs); justify-content: flex-end; }
.ivq__stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: var(--drama-space-md); }
.ivq__clamp { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.5; }
.ivq__ellipsis { display: block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
